import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Link,
} from "@mui/material";
import { apiVariable } from "../../../api/apiVariable";

type Draft = {
  email: string;
  firstName: string;
  lastName: string;
  useFoxCGroupInc: boolean;
  useC1TransportationInc: boolean;

  escrow: boolean;
  escrowAmount: string;
  eldDevice: boolean;
  eldDeviceAmount: string;
  prepass: boolean;
  prepassAmount: string;
  cargoInsurance: boolean;
  cargoInsuranceAmount: string;
  occupationalInsurance: boolean;
  occupationalInsuranceAmount: string;
  parkingSpot: boolean;
  parkingSpotAmount: string;
  driversLegalPlan: boolean;
  driversLegalPlanAmount: string;
  eldDeviceSecurityDeposit: boolean;
  eldDeviceSecurityDepositAmount: string;
  irp: boolean;
  irpAmount: string;
  eldMonitoring: boolean;
  eldMonitoringAmount: string;

  truckDownPayment: string;
  truckLease: string;
  trailerRent: string;
  truckPhysicalInsurance: string;
  trailerPhysicalInsurance: string;
  payRatePercent: string;
};

const initial: Draft = {
  email: "",
  firstName: "",
  lastName: "",
  useFoxCGroupInc: false,
  useC1TransportationInc: false,

  escrow: false,
  escrowAmount: "2500",
  eldDevice: false,
  eldDeviceAmount: "75",
  prepass: false,
  prepassAmount: "20",
  cargoInsurance: false,
  cargoInsuranceAmount: "400",
  occupationalInsurance: false,
  occupationalInsuranceAmount: "200",
  parkingSpot: false,
  parkingSpotAmount: "200",
  driversLegalPlan: false,
  driversLegalPlanAmount: "13.33",
  eldDeviceSecurityDeposit: false,
  eldDeviceSecurityDepositAmount: "250",
  irp: false,
  irpAmount: "60",
  eldMonitoring: false,
  eldMonitoringAmount: "0",

  truckDownPayment: "",
  truckLease: "",
  trailerRent: "",
  truckPhysicalInsurance: "",
  trailerPhysicalInsurance: "",
  payRatePercent: "",
};

export default function DeductionAuthorizationDraftForm() {
  const [data, setData] = useState<Draft>(initial);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState("");

  const text = (e: React.ChangeEvent<HTMLInputElement>) =>
    setData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const check = (e: React.ChangeEvent<HTMLInputElement>) =>
    setData((p) => ({ ...p, [e.target.name]: e.target.checked }));

  const numbers = [
    "escrowAmount",
    "eldDeviceAmount",
    "prepassAmount",
    "cargoInsuranceAmount",
    "occupationalInsuranceAmount",
    "parkingSpotAmount",
    "driversLegalPlanAmount",
    "eldDeviceSecurityDepositAmount",
    "irpAmount",
    "eldMonitoringAmount",
    "truckDownPayment",
    "truckLease",
    "trailerRent",
    "truckPhysicalInsurance",
    "trailerPhysicalInsurance",
    "payRatePercent",
  ] as const;

  const validate = () => {
    const e: Record<string, string> = {};
    if (!data.email) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      e.email = "Invalid";
    if (!data.firstName) e.firstName = "Required";
    if (!data.lastName) e.lastName = "Required";

    numbers.forEach((f) => {
      if (data[f] && isNaN(Number(data[f]))) e[f] = "Number";
    });

    setErrs(e);
    return !Object.keys(e).length;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setErrs({});
    setLink("");

    const body = {
      ...data,
      ...Object.fromEntries(
        numbers.map((f) => [f, +data[f as keyof Draft] || 0]),
      ),
    };

    try {
      const r = await fetch(`${apiVariable}/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("Server error");
      const { token } = await r.json();
      const newLink = `${window.location.origin}/review/deduction-form/${token}`;
      setLink(newLink);
    } catch (err: any) {
      setErrs({ api: err.message });
    } finally {
      setSaving(false);
    }
  };

  const FlagRow = (
    flag: keyof Draft,
    amountName: keyof Draft,
    label: string,
    placeholder = "0.00",
  ) => (
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={12} sm={8}>
        <FormControlLabel
          control={
            <Checkbox
              checked={data[flag] as boolean}
              onChange={check}
              name={flag as string}
            />
          }
          label={label}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          disabled={!data[flag]}
          size="small"
          name={amountName as string}
          value={data[amountName] as string}
          onChange={text}
          placeholder={placeholder}
          error={!!errs[amountName]}
          helperText={errs[amountName]}
        />
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          mt: 4,
          p: { xs: 2, sm: 3 },
          boxShadow: 3,
          borderRadius: 2,
          bgcolor: "#fff",
        }}
      >
        <Typography variant="h4" align="center" gutterBottom>
          Deduction Authorization – Draft
        </Typography>

        {link && (
          <Alert sx={{ mb: 2 }} severity="success">
            Draft saved – share this link with the driver:{" "}
            <Link href={link} target="_blank" rel="noopener">
              {link}
            </Link>
          </Alert>
        )}
        {errs.api && (
          <Alert sx={{ mb: 2 }} severity="error">
            {errs.api}
          </Alert>
        )}

        <form onSubmit={submit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={data.email}
                onChange={text}
                error={!!errs.email}
                helperText={errs.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First name"
                name="firstName"
                value={data.firstName}
                onChange={text}
                error={!!errs.firstName}
                helperText={errs.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last name"
                name="lastName"
                value={data.lastName}
                onChange={text}
                error={!!errs.lastName}
                helperText={errs.lastName}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={data.useFoxCGroupInc}
                    onChange={check}
                    name="useFoxCGroupInc"
                  />
                }
                label="Fox C Group Inc."
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={data.useC1TransportationInc}
                    onChange={check}
                    name="useC1TransportationInc"
                  />
                }
                label="C1 Transportation Inc."
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">
                Deductions (check & edit amount)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              {FlagRow("escrow", "escrowAmount", "Escrow")}
              {FlagRow("eldDevice", "eldDeviceAmount", "ELD Device / month")}
              {FlagRow("prepass", "prepassAmount", "Prepass / month")}
              {FlagRow(
                "cargoInsurance",
                "cargoInsuranceAmount",
                "Cargo Insurance / week",
              )}
              {FlagRow(
                "occupationalInsurance",
                "occupationalInsuranceAmount",
                "Occupational Insurance / month",
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {FlagRow(
                "parkingSpot",
                "parkingSpotAmount",
                "Parking Spot / month",
              )}
              {FlagRow(
                "driversLegalPlan",
                "driversLegalPlanAmount",
                "Driver's Legal Plan / month",
              )}
              {FlagRow(
                "eldDeviceSecurityDeposit",
                "eldDeviceSecurityDepositAmount",
                "ELD Device Security Deposit",
              )}
              {FlagRow("irp", "irpAmount", "IRP / week")}
              {FlagRow(
                "eldMonitoring",
                "eldMonitoringAmount",
                "ELD Monitoring",
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Money-based deductions</Typography>
            </Grid>

            {[
              ["truckDownPayment", "Truck Down Payment"],
              ["truckLease", "Truck Lease (weekly)"],
              ["trailerRent", "Trailer Rent (monthly)"],
              ["truckPhysicalInsurance", "Truck Physical Insurance (monthly)"],
              [
                "trailerPhysicalInsurance",
                "Trailer Physical Insurance (monthly)",
              ],
            ].map(([n, l]) => (
              <Grid item xs={12} sm={6} key={n}>
                <TextField
                  fullWidth
                  label={l}
                  name={n}
                  value={(data as any)[n]}
                  onChange={text}
                  error={!!errs[n]}
                  helperText={errs[n]}
                />
              </Grid>
            ))}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Pay Rate (%)"
                name="payRatePercent"
                value={data.payRatePercent}
                onChange={text}
                error={!!errs.payRatePercent}
                helperText={errs.payRatePercent}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={saving}
                startIcon={saving && <CircularProgress size={20} />}
              >
                {saving ? "Saving…" : "Save draft & generate link"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Container>
  );
}
